import type { SchemaFieldDefinition } from "@erp/shared-types";
import type { SocketType } from "../types";
import { extractionSchemaToOutputSockets } from "./extractionSchemaToSockets";

const SOCKET_ID_REGEX = /^[a-z][a-z0-9_]*$/;

/**
 * Root field names that collide with the node's reserved output socket ids.
 * Schema root fields using these names are ignored for typed sockets and
 * projection (same rule as `http_trigger` excluding `payload`).
 */
const EXTERNAL_HTTP_CALL_RESERVED_RESPONSE_BODY_FIELD_NAMES = [
  "statusCode",
  "responseBody",
  "responseHeaders",
  "rawResponse",
] as const;

/**
 * Eligible root-level fields from a pinned `SchemaDefinitionSnapshot` for
 * mapping JSON response bodies into typed output sockets.
 */
export function eligibleExternalHttpCallResponseBodyRootFields(
  fields: SchemaFieldDefinition[],
): SchemaFieldDefinition[] {
  return fields.filter((f) => {
    if (f.parentFieldId) return false;
    if (!SOCKET_ID_REGEX.test(f.fieldName)) return false;
    if (
      (
        EXTERNAL_HTTP_CALL_RESERVED_RESPONSE_BODY_FIELD_NAMES as readonly string[]
      ).includes(f.fieldName)
    ) {
      return false;
    }
    return true;
  });
}

interface ExternalHttpCallOutputSocketSpec {
  id: string;
  name: string;
  type: SocketType;
  required: boolean;
  description?: string;
}

/**
 * Typed sockets from schema roots only (no reserved sockets).
 */
function schemaRootFieldsToExternalHttpCallTypedOutputSocketSpecs(
  rootFieldsFromSnapshot: SchemaFieldDefinition[],
): ExternalHttpCallOutputSocketSpec[] {
  const eligible = eligibleExternalHttpCallResponseBodyRootFields(
    rootFieldsFromSnapshot,
  );
  return extractionSchemaToOutputSockets(eligible, {
    idPrefix: "",
    mode: "per_field",
    respectFieldRequired: true,
  }).map((s) => ({
    id: s.id,
    name: s.name,
    type: s.type,
    required: s.required,
    description: s.description,
  }));
}

/**
 * Standard reserved outputs appended after schema-derived sockets so the
 * editor and executor agree on ordering (typed fields first, then
 * `statusCode` / `responseBody` / …).
 */
export const EXTERNAL_HTTP_CALL_STANDARD_OUTPUT_SOCKET_SPECS: readonly ExternalHttpCallOutputSocketSpec[] =
  [
    {
      id: "statusCode",
      name: "Status Code",
      type: "number",
      required: false,
      description: "HTTP response status code",
    },
    {
      id: "responseBody",
      name: "Response Body",
      type: "json",
      required: false,
      description: "Parsed response body (JSON when content-type is JSON)",
    },
    {
      id: "responseHeaders",
      name: "Response Headers",
      type: "json",
      required: false,
      description: "Response headers as a flat record",
    },
    {
      id: "rawResponse",
      name: "Raw Response",
      type: "string",
      required: false,
      description: "Raw response body as text",
    },
  ];

export function schemaRootFieldsToExternalHttpCallOutputSockets(
  rootFieldsFromSnapshot: SchemaFieldDefinition[],
): ExternalHttpCallOutputSocketSpec[] {
  return [
    ...schemaRootFieldsToExternalHttpCallTypedOutputSocketSpecs(
      rootFieldsFromSnapshot,
    ),
    ...EXTERNAL_HTTP_CALL_STANDARD_OUTPUT_SOCKET_SPECS,
  ];
}

/** Ids of outputs that exist on every external HTTP call node (schema-typed or not). */
export const EXTERNAL_HTTP_CALL_RESERVED_OUTPUT_SOCKET_IDS: readonly string[] =
  EXTERNAL_HTTP_CALL_STANDARD_OUTPUT_SOCKET_SPECS.map((s) => s.id);
